/**
 * Eros PLP filter tray (ADR-205) - ACTIVE INGREDIENT "periodic table" circles: DATA + PURE LOGIC.
 * No DOM, no network. Loaded before eros-plp-tray-logic.js (see that file for the layer map).
 *
 * Every S&D value of filter.p.m.eros.actives is the GID of an `eros_ingredient` metaobject.
 * Liquid cannot turn that GID into the metaobject (no lookup by id, and `paginate` cannot nest
 * inside the collection's own paginate), so sections/eros-plp-ingredient-index.liquid lists the
 * whole type once (id, canonical_id, name_en, category) and eros-plp-filter-tray.js caches it.
 * This file turns one index row into what the circle needs:
 *   symbol  1-3 Latin characters, from the ONE dictionary SYMBOLS keyed by canonical_id
 *           (curated for the common ingredients), else initials of name_en;
 *   tone    one of 7 tints picked by the ingredient CATEGORY (CSS: --eros-tray-tone-<tone>-*).
 */
(function (root) {
  'use strict';
  var ns = (root.ErosPlpTray = root.ErosPlpTray || {});

  /* canonical_id -> chemical-style symbol (Latin, 1-3 chars). The single place to curate. */
  var SYMBOLS = {
    hyaluronic_acid: 'HA',
    hydrolyzed_hyaluronic_acid: 'HHA',
    niacinamide: 'Ni',
    ceramide: 'Ce',
    salicylic_acid: 'SA',
    glycolic_acid: 'GA',
    lactic_acid: 'LA',
    malic_acid: 'MA',
    tartaric_acid: 'TA',
    azelaic_acid: 'Az',
    kojic_acid: 'KA',
    tranexamic_acid: 'TXA',
    ferulic_acid: 'FA',
    phytic_acid: 'PhA',
    thioctic_acid: 'ALA',
    aha: 'AHA',
    pha: 'PHA',
    retinol: 'Rt',
    retinal: 'Rl',
    retinoid: 'Rd',
    retinyl_palmitate: 'RP',
    retinyl_propionate: 'RPr',
    bakuchiol: 'Bk',
    vitamin_c: 'C',
    vitamin_e: 'E',
    vitamin_d: 'D',
    vitamin_b2: 'B2',
    vitamin_b5: 'B5',
    vitamin_b6: 'B6',
    vitamin_b12: 'B12',
    dexpanthenol: 'B5',
    biotin: 'B7',
    ascorbyl_glucoside: 'AG',
    magnesium_ascorbyl_phosphate: 'MAP',
    three_o_ethyl_ascorbic_acid: 'EAA',
    centella: 'Cx',
    snail_mucin: 'Sn',
    squalane: 'Sq',
    squalene: 'Sl',
    collagen: 'Cg',
    elastin: 'El',
    keratin: 'Kr',
    peptide: 'Pp',
    amino_acids: 'AA',
    glycerin: 'Gl',
    urea: 'Ur',
    allantoin: 'Aln',
    adenosine: 'Ad',
    ectoin: 'Ec',
    betaine: 'Bt',
    xylitol: 'Xy',
    sodium_pca: 'PCA',
    beta_glucan: 'bG',
    zinc: 'Zn',
    zinc_oxide: 'ZnO',
    zinc_pyrithione: 'ZnP',
    copper_pca: 'CuP',
    copper_gluconate: 'Cu',
    titanium_dioxide: 'TiO',
    sulfur: 'S',
    selenium_sulfide: 'Se',
    iron: 'Fe',
    activated_charcoal: 'AC',
    benzoyl_peroxide: 'BPO',
    hydroquinone: 'HQ',
    arbutin: 'Ab',
    alpha_arbutin: 'aAr',
    thiamidol: 'Th',
    glutathione: 'GSH',
    caffeine: 'Cf',
    resveratrol: 'Rv',
    astaxanthin: 'Ax',
    ubiquinone: 'Q10',
    curcumin: 'Cur',
    ergothioneine: 'Eg',
    carnosine: 'Cn',
    quercetin: 'Qc',
    hesperidin: 'Hs',
    phloretin: 'Pl',
    egcg: 'EGC',
    superoxide_dismutase: 'SOD',
    nicotinamide_adenine_dinucleotide: 'NAD',
    salmon_dna: 'DNA',
    cholesterol: 'Ch',
    phytosphingosine: 'Ps',
    butyl_resorcinol: 'BR',
    hexylresorcinol: 'Hx',
    phenylethyl_resorcinol: 'PR',
    piroctone_olamine: 'PO',
    menthol: 'Mn',
    sodium_bicarbonate: 'NaB',
    epsom_salt: 'MgS',
    shea_butter: 'Sh',
    mango_butter: 'Mg',
    cocoa_butter: 'Cb',
    coconut_oil: 'Cc',
    avocado_oil: 'Av',
    grape_seed_oil: 'Gs',
    jojoba_oil: 'Jo',
    argan_oil: 'Ar',
    castor_oil: 'Cr',
    olive_oil: 'Ol',
    sweet_almond_oil: 'Am',
    macadamia_oil: 'Mc',
    sunflower_seed_oil: 'Sf',
    soybean_oil: 'Sy',
    linseed_oil: 'Ls',
    beeswax: 'Bw',
    aloe_vera: 'Al',
    tea_tree: 'TT',
    green_tea: 'GT',
    chamomile: 'Cm',
    honey: 'Hn',
    propolis: 'Pr',
    licorice: 'Lq',
    mugwort: 'Mw',
    ginseng: 'Gn',
    rosemary: 'Rm',
    lavender_oil: 'Lv',
    lavender_extract: 'Lv',
    oat_extract: 'Ot',
    rice_extract: 'Rc',
    heartleaf: 'Hl',
    bisabolol: 'Bs',
    witch_hazel: 'WH'
  };

  /* ingredient category (eros_ingredient.category) -> tint. Unknown categories fall to "care". */
  var TONES = ['hydrate', 'renew', 'antioxidant', 'brighten', 'soothe', 'oil', 'care'];
  var CATEGORY_TONE = {
    hydrator: 'hydrate',
    hydrator_keratolytic: 'hydrate',
    humectant: 'hydrate',
    barrier: 'hydrate',
    exfoliant_aha: 'renew',
    exfoliant_bha: 'renew',
    exfoliant_pha: 'renew',
    exfoliant_physical: 'renew',
    retinoid: 'renew',
    retinoid_alt: 'renew',
    antioxidant: 'antioxidant',
    vitamin: 'antioxidant',
    brightening: 'brighten',
    brightening_rx: 'brighten',
    antioxidant_brightening: 'brighten',
    acid_brightening: 'brighten',
    soothing: 'soothe',
    repair: 'soothe',
    emollient: 'oil',
    peptide: 'care',
    antibacterial: 'care',
    sebum_control: 'care',
    astringent: 'care',
    uv_filter: 'care',
    scalp: 'care',
    oral_care: 'care',
    antiperspirant: 'care',
    active_misc: 'care',
    other: 'care'
  };

  function toneFor(category) {
    var key = String(category || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(CATEGORY_TONE, key) ? CATEGORY_TONE[key] : 'care';
  }

  /* Initials of the English name: one word -> "Ad", several -> up to three capitals ("AT"). */
  function initialsOf(text) {
    var words = String(text || '').split(/[^A-Za-z0-9]+/);
    var letters = [];
    for (var i = 0; i < words.length; i++) if (/^[A-Za-z]/.test(words[i])) letters.push(words[i]);
    if (!letters.length) return '';
    if (letters.length === 1) return letters[0].charAt(0).toUpperCase() + letters[0].charAt(1).toLowerCase();
    var out = '';
    for (var j = 0; j < letters.length && j < 3; j++) out += letters[j].charAt(0).toUpperCase();
    return out;
  }

  function symbolFor(canonicalId, nameEn) {
    var cid = String(canonicalId || '');
    if (cid && Object.prototype.hasOwnProperty.call(SYMBOLS, cid)) return SYMBOLS[cid];
    return initialsOf(nameEn) || initialsOf(cid.replace(/_/g, ' '));
  }

  /** "gid://shopify/Metaobject/250682048799" -> "250682048799" ('' when it has no numeric tail). */
  function idOfGid(gid) {
    var m = /(\d+)\s*$/.exec(String(gid || ''));
    return m ? m[1] : '';
  }

  /**
   * Merge one page of eros-plp-ingredient-index.liquid JSON into a lookup { id: {c, e, k} }.
   * page = { rows:[{i, c, e, k}], next }. Returns the same (mutated) lookup.
   */
  function mergeIndex(lookup, page) {
    var rows = (page && page.rows) || [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var id = String(r && r.i !== undefined && r.i !== null ? r.i : '');
      if (id) lookup[id] = { c: String(r.c || ''), e: String(r.e || ''), k: String(r.k || '') };
    }
    return lookup;
  }

  /**
   * What the circle needs for one S&D value. `lookup` may be empty (index not loaded yet or
   * failed): the circle then shows a neutral tint with the first character of the label.
   * Returns { symbol, tone, alt } - alt is extra search text (English name + canonical words).
   */
  function describe(gid, label, lookup) {
    var hit = lookup && lookup[idOfGid(gid)];
    if (!hit) return { symbol: String(label || '').charAt(0), tone: 'plain', alt: '' };
    return {
      symbol: symbolFor(hit.c, hit.e),
      tone: toneFor(hit.k),
      alt: (hit.e + ' ' + hit.c.replace(/_/g, ' ')).replace(/^\s+|\s+$/g, '')
    };
  }

  var api = {
    SYMBOLS: SYMBOLS,
    TONES: TONES,
    CATEGORY_TONE: CATEGORY_TONE,
    toneFor: toneFor,
    initialsOf: initialsOf,
    symbolFor: symbolFor,
    idOfGid: idOfGid,
    mergeIndex: mergeIndex,
    describe: describe
  };
  ns.actives = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
